import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MuiCloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import styled from 'styled-components';

/**
 * Component for the title bar of a dialog.
 * Add children to add custom JSX to the left of the close button.
 *
 * Props: { title, handleClose }
 * @param {{
 *     title: string,
 *     handleClose: () => void,
 *     children: JSX.Element,
 *     typeMeta?: { Icon: React.ComponentType, color: string },
 * }}
 */
const DialogHeader = styled(({
    className, handleClose, title, children, draggable, typeMeta,
}) => (
    // data-drag-handle is used by DraggablePaper in DialogProvider to identify the drag target.
    // Adding it here makes any dialog that uses DraggablePaper draggable from the title bar.
    <DialogTitle
        className={className}
        data-drag-handle="true"
        sx={{
            // Prevent the browser from claiming finger drags as scroll/pan gestures,
            // which would fire pointercancel and end the drag after a short distance.
            touchAction: draggable ? 'none' : undefined,
            // Compact the title bar for alert-family dialogs so the message body
            // dominates rather than the chrome.
            ...(typeMeta && { py: 0.75, px: 2 }),
        }}
    >
        <Grid container wrap="nowrap" alignItems="center">
            {typeMeta && (
                <DragIndicatorIcon
                    aria-hidden
                    fontSize="medium"
                    sx={{
                        color: typeMeta.color,
                        opacity: 0.9,
                        alignSelf: 'center',
                        flexShrink: 0,
                        ml: 0.5,
                        mr: 1.5,
                    }}
                />
            )}
            <Grid
                container
                sx={{
                    alignItems: 'center',
                    justifyContent: typeMeta ? 'center' : 'flex-start',
                    flex: '1 1 auto',
                }}
            >
                <Typography
                    variant={typeMeta ? 'h6' : 'h5'}
                    className="title"
                    sx={{ textAlign: typeMeta ? 'center' : 'left' }}
                >
                    { title }
                </Typography>
            </Grid>
            <Grid container sx={{ alignContent: 'center', justifyContent: 'flex-end', flex: '0 0 auto' }}>
                {children}
            </Grid>
            <IconButton onClick={handleClose} size="small" aria-label="close modal">
                <CloseRoundedIcon />
            </IconButton>
        </Grid>
    </DialogTitle>
))`
  /* Alert-family dialogs get a neutral light-grey title bar so the type accent
     (top stripe on the Paper + colored grip glyph) carries the type identity.
     Feature dialogs keep the original solid primary title bar with white text. */
  color: ${({ theme, typeMeta }) => (typeMeta ? theme.palette.text.primary : 'white')};
  background-color: ${({ theme, typeMeta }) => (
    typeMeta ? theme.palette.grey[300] : theme.palette.primary.main
)};

  /* Override the close-icon color for alert-family dialogs so it reads on the
     light-grey title bar. Feature dialogs keep the white close icon. */
  ${({ theme, typeMeta }) => typeMeta && `
    & button svg {
      color: ${theme.palette.text.primary};
    }
  `}

  /* Use 'grab' for alert-family dialogs and the legacy 'move' for other
     draggable dialogs (which opted in before this change). */
  cursor: ${({ draggable, typeMeta }) => {
    if (!draggable) return 'default';
    return typeMeta ? 'grab' : 'move';
}};

  /* While the parent DraggablePaper is mid-drag, swap to 'grabbing' so the
     cursor reflects the in-progress action. */
  [data-dragging="true"] & {
    cursor: ${({ draggable }) => (draggable ? 'grabbing' : 'default')};
  }

  /* Restore the default pointer cursor on interactive controls in the header
     so the move/grab cursor does not bleed onto the close button or other actions. */
  & button, & a {
    cursor: pointer;
  }

  & .title {
    font-weight: 700;
  }
`;

const CloseRoundedIcon = styled(MuiCloseRoundedIcon)`
  color: white;
`;

/*
 * Design exploration log — alert-family dialog header styling
 * -----------------------------------------------------------
 * The alert/confirm/warning dialogs share this DialogHeader with feature
 * dialogs (Import, Export, Screening, etc.). Goal of this exploration was to
 * make alerts visually distinct from feature dialogs and signal that the title
 * bar is a drag handle, without restyling the feature dialogs themselves.
 *
 * Things we tried:
 *   1. Solid type-colored title bar (info.main for alert, warning.main for
 *      confirm/warning) with white text and a 4px top accent stripe.
 *      Verdict: too loud — alerts shouted instead of speaking.
 *
 *   2. Reverted the title bar to primary, centered the title text, bumped
 *      the top stripe to 6px.
 *      Verdict: cleaner, but the title bar still felt the same as feature
 *      dialogs aside from the stripe.
 *
 *   3. Light grey title bar (grey.100) with dark text, accent color moved
 *      to the DragIndicator glyph + 6px top stripe.
 *      Verdict: right direction but contrast was a bit weak.
 *
 *   4. Darker grey (grey.300), close-icon override scoped to typed dialogs
 *      only so feature dialogs keep their white close icon, compacted the
 *      bar with py/px and shrunk the title to h6.
 *      Verdict: shipping target.
 *
 * Where the type accent lives in the final design:
 *   - 6px top stripe on the Paper (DraggablePaper in DialogProvider.jsx).
 *   - DragIndicator glyph at the left of the title, colored with typeMeta.color.
 *   - Large type icon next to the body content (existing TYPE_META.Icon).
 *
 * The title bar itself is a neutral grey so the accent reads as deliberate
 * trim rather than wholesale color. Feature dialogs are unaffected because
 * every typed style is gated on `typeMeta` being defined.
 */

export default DialogHeader;
